<template>
  <div class="event-card">
    <div>
      <p>{{ helpers.getLocalizedField(message, 'title') }}</p>
      <button @click="showModal" class="btn btn-primary-red">{{ $t('home.modal-messages.read-more') }}</button>
    </div>
    <b-modal
      @shown="shown"
      :hide-footer="true"
      :hide-header="true"
      :no-close-on-esc="mustAgree || isEvent"
      :no-close-on-backdrop="mustAgree || isEvent"
      :hide-header-close="mustAgree || isEvent"
      size="lg"
      content-class="modal-blue"
      :id="'modal-' + message.modal_messageID">
      <div class="row modal-message-content">
        <div class="col mb-5 pb-5">
          <h4 class="mb-5 text-white">{{ helpers.getLocalizedField(message, 'title') }}</h4>
          <p class="my-4 text-white" v-html="helpers.getLocalizedField(message, 'text')"></p>
        </div>
      </div>

      <!-- Type 3: Attendance — Yes/No (once) + QR, never auto-dismissed -->
      <div v-if="isAttendance" class="row justify-content-center mb-4">
        <div class="col-12 text-center">
          <!-- Buttons only while unanswered -->
          <div v-if="attendingLocal === null" class="d-flex justify-content-center mb-3">
            <button
              type="button"
              class="btn btn-light-bold mr-3"
              :disabled="attendanceSaving"
              @click="saveAttendance(true)"
            >{{ $t('home.modal-messages.attending-yes') }}</button>
            <button
              type="button"
              class="btn btn-outline-light"
              :disabled="attendanceSaving"
              @click="saveAttendance(false)"
            >{{ $t('home.modal-messages.attending-no') }}</button>
          </div>
          <div v-if="attendanceSaving" class="text-white mb-3">
            <span class="spinner-border spinner-border-sm mr-1"></span>
          </div>
          <!-- Confirmation text after answering -->
          <p v-if="attendingLocal === true" class="text-white mb-3">{{ $t('home.modal-messages.confirmed-yes') }}</p>
          <p v-if="attendingLocal === false" class="text-white mb-3">{{ $t('home.modal-messages.confirmed-no') }}</p>
          <!-- QR shown only when attending (true or null), hidden when No -->
          <img
            v-if="message.qr_code && attendingLocal !== false"
            :src="message.qr_code"
            alt="QR"
            class="qr-image mt-2"
          />
        </div>
      </div>

      <!-- Type 2: Event invitation flow -->
      <div v-if="isEvent">
        <!-- Step 1: Yes / No -->
        <div v-if="eventStep === 1" class="row justify-content-center mb-4">
          <div class="col-auto">
            <button type="button" class="btn btn-light-bold mr-3" @click="onEventYes">{{ $t('home.modal-messages.yes') }}</button>
            <button type="button" class="btn btn-outline-light" @click="onEventNo">{{ $t('home.modal-messages.no') }}</button>
          </div>
        </div>

        <!-- Step 2: Email input -->
        <div v-if="eventStep === 2" class="row justify-content-center mb-4">
          <div class="col-12 col-sm-8">
            <p class="text-white mb-3">{{ $t('home.modal-messages.enter-email') }}</p>
            <input
              v-model="eventEmail"
              type="email"
              class="form-control mb-3"
              :placeholder="$t('home.modal-messages.email-placeholder')"
            />
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-light-bold mr-2" @click="onEventSend" :disabled="!eventEmail || eventSending">
                <span v-if="eventSending"><span class="spinner-border spinner-border-sm mr-1"></span>{{ $t('home.modal-messages.sending') }}</span>
                <span v-else>{{ $t('home.modal-messages.send') }}</span>
              </button>
              <button type="button" class="btn btn-outline-light" @click="eventStep = 1" :disabled="eventSending">{{ $t('home.modal-messages.back') }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Type 1: Regular agree button -->
      <div v-if="mustAgree" class="row">
        <div class="col mt-0 pt-3 mt-sm-5 pt-sm-5 mb-3">
          <button type="button" class="btn btn-light-bold" @click="onAgreeClick">{{ $t('home.modal-messages.agree') }}</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import moment from 'moment';
  import axios from 'axios';
  import { mapGetters } from 'vuex';

  export default {
    name: 'modal-message',

    props: ['message'],

    data() {
      return {
        eventStep: 1,
        eventEmail: '',
        eventSending: false,
        attendingLocal: this.message.attending,
        attendanceSaving: false,
      };
    },

    computed: {
      mustAgree() {
        return this.message.recipients[0].seen == 0 && Number(this.message.agree_button) === 1;
      },

      isEvent() {
        return this.message.recipients[0].seen == 0 && Number(this.message.agree_button) === 2;
      },

      isAttendance() {
        return Number(this.message.agree_button) === 3;
      },

      ...mapGetters({
        mustUploadDocs: 'auth/mustUploadDocs'
      })
    },

    mounted() {
      const now = moment();
      const from = moment(this.message.date_from);
      const to = moment(this.message.date_to);

      if(now <= to && now >= from && this.message.recipients[0].seen == 0 && !this.mustUploadDocs) {
        this.showModal();
      }
    },

    methods: {
      showModal() {
        this.eventStep = 1;
        this.eventEmail = '';
        this.$bvModal.show('modal-' + this.message.modal_messageID);
      },

      shown() {
        if(!parseInt(this.message.agree_button)) {
          this.seen();
        }
      },

      onAgreeClick() {
        this.seen()
        .then(() => {
          this.$bvModal.hide('modal-' + this.message.modal_messageID);
        });
      },

      onEventYes() {
        this.eventStep = 2;
      },

      onEventNo() {
        this.seen().then(() => {
          this.$bvModal.hide('modal-' + this.message.modal_messageID);
        });
      },

      onEventSend() {
        if (!this.eventEmail || this.eventSending) return;
        this.eventSending = true;
        axios.post('/api/popup-messages/' + this.message.modal_messageID + '/event-register', {
          email: this.eventEmail,
        })
        .then(() => {
          this.seen().then(() => {
            this.$bvModal.hide('modal-' + this.message.modal_messageID);
            this.$swal({
              icon: 'success',
              text: this.$t('home.modal-messages.email-sent'),
              timer: 2000,
              showConfirmButton: false,
            });
            this.eventSending = false;
          });
        })
        .catch(() => {
          this.eventSending = false;
        });
      },

      saveAttendance(attending) {
        if (this.attendanceSaving) return;
        this.attendanceSaving = true;
        const params = new URLSearchParams();
        params.append('attending', attending ? 1 : 0);
        axios.post(
          '/api/popup-messages/' + this.message.modal_messageID + '/event-answer',
          params
        )
        .then((response) => {
          this.attendingLocal = attending;
          const msg = response.data && response.data.message
            ? response.data.message
            : this.$t('home.modal-messages.attendance-saved');
          this.$swal({
            icon: 'success',
            text: msg,
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.$bvModal.hide('modal-' + this.message.modal_messageID);
          } else {
            console.error('event-answer error', err);
          }
        })
        .finally(() => {
          this.attendanceSaving = false;
        });
      },

      seen() {
        if(this.message.recipients[0].seen == 0) {
          return this.$store.dispatch('popup_messages/seen', this.message.modal_messageID);
        }

        return new Promise(resolve => { resolve(); });
      }
    }
  };
</script>

<style scoped>
  .qr-image {
    width: 240px;
    height: 240px;
    image-rendering: pixelated;
    display: block;
    margin: 0 auto;
  }
</style>
